import uiCore = require("./uicore");
import coreSettings = require("./coresettings");
import breeze = require("breeze-client");
import { Moment } from "moment";
import moment = require("moment");

// SqlDateTime.MaxValue
// The maximum valid date for a SqlDateTime structure is December 31, 9999
// 9999-12-31 23:59:59.997
export var DateMaxInfinityLocal: Date = new Date(9999, 11, 31, 23, 59, 59, 997);
export var DateMaxInfinity: Date = new Date(Date.UTC(9999, 11, 31, 23, 59, 59, 997)); // Month zero based

// SqlDateTime.MinValue
// The minimum valid date for a SqlDateTime structure is January 1, 1753.
export var DateMinInfinityLocal: Date = new Date(1753, 0, 1);
export var DateMinInfinity: Date = new Date(Date.UTC(1753, 0, 1));

export function roundDay(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function firstDayOfMonth(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), 1);
}

export function lastDayOfMonth(date: Date) {
    let d = firstDayOfMonth(date);
    return new Date(d.getFullYear(), d.getMonth() + 1, d.getDate() - 1);
}

export function isPosInfinity(date: Date): boolean {
    return date >= DateMaxInfinity;
}

export function isNegInfinity(date: Date): boolean {
    return date <= DateMinInfinity;
}

export function getToday(): Date {
    let date = new Date();
    return roundDay(date);
}

export function getTodayUTC(): Date {
    let date = getToday();
    return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
}

export function getTodayOrNull(getNull: boolean): Date {
    return getNull ? null : getToday();
}

export function addTimezoneOffset(d: Date): Date {
    return d ? new Date(d.getTime() + getTimezoneOffsetMilliseconds(d)) : d;
}

export function subtractTimezoneOffset(d: Date): Date {
    return d ? new Date(d.getTime() - getTimezoneOffsetMilliseconds(d)) : d;
}

function getTimezoneOffsetMilliseconds(d: Date): number {
    return d.getTimezoneOffset() * 60 * 1000;
}

export function getStartOfToday(): Date {
    return getStartOfDay(moment());
}

export function getStartOfDay(momentDate: Moment): Date {
    return getMomentStartOfDay(momentDate).toDate();
}

export function getMomentStartOfToday(): Moment {
    return getMomentStartOfDay(moment());
}

export function getMomentStartOfDay(momentDate: Moment): Moment {
    return moment(momentDate).utc(true).startOf("day");
}

export enum CompetenceState { Valid, Warning, Invalid, Expired, Future }

export function computeCompetenceState(from: Date, to: Date, reference: Date, warningTimeNrMonths?: number): CompetenceState {
    var warningTime: Date = null;
    if (warningTimeNrMonths && warningTimeNrMonths > 0) {
        try {// May cause an invalid Date
            warningTime = new Date(to.getTime());
            warningTime.setMonth(to.getMonth() - warningTimeNrMonths);
        }
        catch (error) {
            warningTime = null;
        }
    }
    else {
        warningTime = null;
    }

    if (from > to) return CompetenceState.Invalid;
    else if (to <= reference) return CompetenceState.Expired;
    else if (from > reference) return CompetenceState.Future;
    else if (warningTime && reference >= warningTime) return CompetenceState.Warning;
    else return CompetenceState.Valid;
}

export interface ItemWithSortTagAndName {
    SortTag: string;
    Name: string;
}

export function orderBySortTagThenName<T extends ItemWithSortTagAndName>(items: T[]): T[] {
    const arrNull: T[] = [];
    const arrNotNull: T[] = [];

    for (const item of items) {
        if (!item.SortTag) {
            arrNull.push(item);
        } else {
            arrNotNull.push(item);
        }
    }

    arrNull.sort((a, b) => compareStrings(a.Name, b.Name));
    arrNotNull.sort((a, b) => compareStrings(a.SortTag, b.SortTag, () => compareStrings(a.Name, b.Name)));

    items = [];
    items = arrNotNull.concat(arrNull);

    return items;
}

export function filterBy<T>(items: T[], filter: string, propertyName: string): T[] {

    if (!filter) {
        return items;
    }

    // filter items array, items which match and return true will be kept, false will be filtered out
    return items.filter(item => matches(item, filter, propertyName ?? "Name"));
}

function matches(item, searchText: string, filterByProperty: string): boolean {
    let value: string = item[filterByProperty];

    if (!value) {
        return false;
    }

    // Lowercased equal comparison
    return (value.toLowerCase().indexOf(searchText.toLowerCase() || "") !== -1);
}

export function selectMany<TIn, TOut>(input: TIn[], selectListFn: (t: TIn) => TOut[]): TOut[] {
    return input.reduce((out, inx) => {
        out.push(...selectListFn(inx));
        return out;
    }, new Array<TOut>());
}

export function distinct<TIn>(input: TIn[], areEqual: (v1: TIn, v2: TIn) => boolean) {
    return input.filter((v1, i, a) => a.findIndex(v2 => areEqual(v1, v2)) === i);
}

export function compareStrings(a: string, b: string, callbackIfEqual?: () => number): number {
    if (!a && !b) {
        return 0;
    }

    if (!b) {
        return -1;
    }

    if (!a) {
        return 1;
    }

    const value = a.localeCompare(b);
    return value !== 0 ? value : (callbackIfEqual?.() ?? 0);
}

interface IDateFormatter {
    (date: Date): string;
}

var _crewDateFilter: uiCore.IFilterClassType<IDateFormatter> = {
    ComponentDescription: {
        AngularDependencies: [uiCore.FilterDependency],
        AngularInjectionId: "crewdate",
        FilterConstructor: ($filter: ng.IFilterService) => (date: Date) => {
            if (!date) return "";
            if (date <= DateMinInfinity) return "";
            if (date >= DateMaxInfinity) return "";
            return $filter("date")(date, "dd-MM-yyyy");
        }
    }
}

interface IActiveItem {
    Active: boolean;
}

interface IActiveFilter {
    (items: IActiveItem[]): IActiveItem[];
}

var _crewActiveFilter: uiCore.IFilterClassType<IActiveFilter> = {
    ComponentDescription: {
        AngularDependencies: [],
        AngularInjectionId: "crewactive",
        FilterConstructor: () => (items: IActiveItem[]) => {
            if (!items) return null;
            if (coreSettings.applicationSettings.showInactive) return items;
            var result = [];
            for (var i = 0; i < items.length; i++) {
                if (items[i].Active == true) result.push(items[i]);
            }
            return result;
        }
    }
}

interface IActiveFuncFilter<T> {
    (items: T[], isActiveFunc: (item: T) => boolean): T[];
}

var _crewActiveFuncFilter: uiCore.IFilterClassType<IActiveFuncFilter<any>> = {
    ComponentDescription: {
        AngularDependencies: [],
        AngularInjectionId: "crewactivefunc",
        FilterConstructor: () => (items: IActiveItem[], isActiveFunc: (item: any) => boolean) => {
            if (!items) return null;
            if (coreSettings.applicationSettings.showInactive) return items;
            var result = [];
            for (var i = 0; i < items.length; i++) {
                if (isActiveFunc(items[i])) result.push(items[i]);
            }
            return result;
        }
    }
}

interface IBreezeEntityWithName extends breeze.Entity {
    Name: string;
}

interface IBreezeEntityWithNameFilter {
    (items: IBreezeEntityWithName[], searchString: string): IBreezeEntityWithName[];
}

var _crewNameFilter: uiCore.IFilterClassType<IBreezeEntityWithNameFilter> = {
    ComponentDescription: {
        AngularDependencies: [],
        AngularInjectionId: "crewnamefilter",
        FilterConstructor: () => (items: IBreezeEntityWithName[], searchString: string) => {
            if (!items) return null;
            if (!searchString || searchString == "") return items;
            var query = breeze.EntityQuery.fromEntities(items)
                .where("Name", "contains", searchString)
                .orderBy("Name");
            return <any>query.executeLocally();
        }
    }
}

//interface IBreezeEntityWithName extends breeze.Entity {
//    Name: string;
//}

interface IStringContainsFilter<T> {
    (items: T[], searchString: string, selector: (item: T) => string): T[];
}

var _crewStringContainsFilter: uiCore.IFilterClassType<IStringContainsFilter<any>> = {
    ComponentDescription: {
        AngularDependencies: [],
        AngularInjectionId: "crewstringcontains",
        FilterConstructor: () => (items, searchString, selector) => {
            if (!items) return null;
            if (!searchString || searchString == "") return items;
            searchString = searchString.toLowerCase();
            var result = [];
            for (var i = 0; i < items.length; i++) {
                if (selector(items[i]).toLowerCase().indexOf(searchString) != -1) result.push(items[i]);
            }
            return result;
        }
    }
}

export function hashGetArrayDiff<T_M, T_D>(mainCollection: T_M[], compareCollection: T_D[], idSelectorM: (item: T_M) => number | string, idSelectorD: (item: T_D) => number | string): T_D[] {
    var idsComp = {};
    for (var i = 0; i < compareCollection.length; i++) {
        idsComp[<any>idSelectorD(compareCollection[i])] = compareCollection[i];
    }

    for (var i = 0; i < mainCollection.length; i++) {
        var id = <any>idSelectorM(mainCollection[i]);
        if ((<Object>idsComp).hasOwnProperty(id)) {
            delete idsComp[id];
        }
    }
    var idsDiff = Object.getOwnPropertyNames(idsComp);
    var diffItems = [];
    for (var i = 0; i < idsDiff.length; i++) {
        diffItems.push(idsComp[idsDiff[i]]);
    }
    return diffItems;
}

//export function hashArrayDiffApply<T_M, T_D>(mainCollection: T_M[], compareCollection: T_D[], idSelectorM: (item: T_M) => number|string, idSelectorD: (item: T_D) => number|string, applyDiff: (m: T_M, d: T_D) => void) {
//    var idsComp = {};
//    for (var i = 0; i < compareCollection.length; i++) {
//        idsComp[<any>idSelectorD(compareCollection[i])] = { d: compareCollection[i], m: null };
//    }

//    for (var i = 0; i < mainCollection.length; i++) {
//        var id = <any>idSelectorM(mainCollection[i]);
//        if ((<Object>idsComp).hasOwnProperty(id)) {
//            delete idsComp[id];
//        } else {
//            idsComp[id].m = mainCollection[i];
//        }
//    }
//    var idsDiff = Object.getOwnPropertyNames(idsComp);

//    for (var i = 0; i < idsDiff.length; i++) {
//        applyDiff(idsComp[idsDiff[i]].m, idsComp[idsDiff[i]].d);
//    }
//}

export function hashArrayFindApply<T_M, T_F>(mainCollection: T_M[], itemsFind: T_F[], idSelectorM: (item: T_M) => number | string, idSelectorF: (item: T_F) => number | string, applyOnItemFound: (m: T_M, f: T_F) => boolean) {
    var idsComp = {};
    for (var i = 0; i < itemsFind.length; i++) {
        idsComp[<any>idSelectorF(itemsFind[i])] = itemsFind[i];
    }

    for (var i = 0; i < mainCollection.length; i++) {
        var id = <any>idSelectorM(mainCollection[i]);
        if ((<Object>idsComp).hasOwnProperty(id)) {
            // Return id applyOnItemFound returns false
            if (!applyOnItemFound(mainCollection[i], idsComp[id])) return;
        }
    }
}

function hashMergeArrays<T>(largerCollection: T[], smallerCollection: T[], idSelector: (item: T) => number | string) {
    var idsSmaller = {};
    for (var i = 0; i < smallerCollection.length; i++) {
        idsSmaller[<any>idSelector(smallerCollection[i])] = smallerCollection[i];
    }

    for (var i = 0; i < largerCollection.length; i++) {
        var id = <any>idSelector(largerCollection[i]);
        if ((<Object>idsSmaller).hasOwnProperty(id)) {
            delete idsSmaller[id];
        }
    }
    var idsAdd = Object.getOwnPropertyNames(idsSmaller);
    var extraItems = [];
    for (var i = 0; i < idsAdd.length; i++) {
        extraItems.push(idsSmaller[idsAdd[i]]);
    }
    return largerCollection.concat(extraItems);
}

interface IArrayMergeFilter<T> {
    (items: T[], extraItems: T[], idSelector: (item: T) => string | number): T[];
}

var _crewArrayMergeFilter: uiCore.IFilterClassType<IArrayMergeFilter<any>> = {
    ComponentDescription: {
        AngularDependencies: [],
        AngularInjectionId: "crewarraymerge",
        FilterConstructor: () => (items, extraItems, idSelector) => {
            if (!items) return null;
            if (!extraItems) return items;
            return (items.length > extraItems.length) ? hashMergeArrays(items, extraItems, idSelector) : hashMergeArrays(extraItems, items, idSelector)
        }
    }
}

var _crewDateIsNegInfinity = uiCore.simpleFilter({
    AngularInjectionId: "crewdateisneginf",
    Filter: (date: Date): boolean => {
        return date <= DateMinInfinity;
    }
});

var _crewDateIsPosInfinity = uiCore.simpleFilter({
    AngularInjectionId: "crewdateisposinf",
    Filter: (date: Date): boolean => {
        return date >= DateMaxInfinity;
    }
});

var _crewDateIsNegInfinityUTC = uiCore.simpleFilter({
    AngularInjectionId: "crewdateisneginfutc",
    Filter: (date: Date): boolean => {
        return date <= DateMinInfinityLocal;
    }
});

var _crewDateIsPosInfinityUTC = uiCore.simpleFilter({
    AngularInjectionId: "crewdateisposinfutc",
    Filter: (date: Date): boolean => {
        return date >= DateMaxInfinityLocal;
    }
});

var _crewCompetenceColorFilter = uiCore.simpleFilter({
    AngularInjectionId: "crewcompetencecolor",
    Filter: (state: CompetenceState) => {
        switch (state) {
            case CompetenceState.Valid:
                return "#00FF00";
            case CompetenceState.Warning:
                return "#FFFF00";
            case CompetenceState.Expired:
                return "#FF0000";
            case CompetenceState.Future:
                return "#0000FF";
            case CompetenceState.Invalid:
                return "#000000";
            default:
                return "#000000";
        }
    }
});

export var commonFilters: uiCore.IFilterModuleDescription[] = [
    {
        AngularInjectionId: "crewcommonfilters",
        Filters: [
            _crewDateFilter,
            _crewNameFilter,
            _crewStringContainsFilter,
            _crewArrayMergeFilter,
            _crewActiveFilter,
            _crewActiveFuncFilter,
            _crewCompetenceColorFilter,
            _crewDateIsNegInfinity,
            _crewDateIsPosInfinity,
            _crewDateIsNegInfinityUTC,
            _crewDateIsPosInfinityUTC
        ]
    },
];

var _crewDateUTCConverter: uiCore.IConverterClassType<Date, Date> = {
    ComponentDescription: {
        AngularDependencies: [],
        DirectiveId: "crewtwowayutc",
        ConvertFromSource: (src) => {
            if (src) {
                return new Date(src.getTime() + src.getTimezoneOffset() * 60000);
            }
            else return src;
        },
        ConvertToSource: (tgt) => {
            return new Date(tgt.getTime() - tgt.getTimezoneOffset() * 60000);
        }
    }
}

export var commonConverteres: uiCore.IConverterModuleDescription[] = [
    {
        AngularInjectionId: "crewcommonconverters",
        Converters: [
            _crewDateUTCConverter,
        ]
    },
];


// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/@angularclass/hmr-loader!./node_modules/tslint-loader!./client/app/core/datacore.ts